/**
 * K-OS Health → Exercises (browse catalog).
 *
 * Search + horizontal muscle rail + advanced Filters modal + tile grid + detail
 * modal. Ported from the K-OS Health design (screens-health-exercises.jsx) and
 * driven by the real Health API hooks.
 *
 * Filtering is split between server and client:
 *   - The advanced filters from the FiltersModal (equipment ids + muscle/body-part
 *     slugs) and the search text are passed to `useExercises` and applied
 *     server-side.
 *   - The rail `group` (Favorites / Cardio / a single muscle slug) is a quick
 *     client-side narrowing on top of the returned list.
 *
 * Add-to-workout is a placeholder here — wired in a later task.
 */

import { useState, useCallback } from 'react';
import { Icon } from '@k-os/ui';
import {
  useExercises,
  useEquipment,
  useFavoriteExerciseIds,
  useToggleFavorite,
  useArchiveExercise,
  useWorkoutTemplates,
  useReplaceTemplateExercises,
  type ExerciseDto,
  type EquipmentDto,
  type WorkoutTemplateDto,
} from '../../../api/health';
import {
  MUSCLE_GROUPS,
  muscleLabel,
  muscleIconSrc,
  typeLabel,
  levelLabel,
  equipmentName,
  ExerciseFigure,
  EquipGlyph,
} from './atoms';
import { ExerciseEditor } from '../workouts/ExerciseEditor';
import baseStyles from '../health.module.css';
import styles from './exercises.module.css';

/** Rail selection: a special bucket or a single muscle slug. */
type RailGroup = 'favorites' | 'cardio' | string | null;

/** Rail items in display order: Favorites, Cardio, then the 13 muscle groups. */
const RAIL_ITEMS: Array<{ id: string; label: string; icon: string }> = [
  { id: 'favorites', label: 'Favorites', icon: '/health/muscles/favorites.svg' },
  { id: 'cardio', label: 'Cardio', icon: '/health/muscles/cardio.svg' },
  ...MUSCLE_GROUPS.map((m) => ({
    id: m.slug,
    label: m.label,
    // Every MUSCLE_GROUPS slug has a matching SVG; fall back to favorites svg
    // defensively so `icon` is always a string.
    icon: muscleIconSrc(m.slug) ?? '/health/muscles/favorites.svg',
  })),
];

/** Which exercise editor overlay is open, if any. */
type EditorState = null | { kind: 'new' } | { kind: 'edit'; exercise: ExerciseDto };

export function HealthExercisesScreen() {
  const [q, setQ] = useState('');
  const [group, setGroup] = useState<RailGroup>(null);
  const [detail, setDetail] = useState<ExerciseDto | null>(null);
  const [editor, setEditor] = useState<EditorState>(null);
  const [addToWorkout, setAddToWorkout] = useState<ExerciseDto | null>(null);

  // Advanced filters — `draft*` is edited inside the modal and committed to
  // `applied*` on Apply. Applied filters drive the server query.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftEquip, setDraftEquip] = useState<string[]>([]);
  const [draftParts, setDraftParts] = useState<string[]>([]);
  const [appliedEquip, setAppliedEquip] = useState<string[]>([]);
  const [appliedParts, setAppliedParts] = useState<string[]>([]);
  const activeFilterCount = appliedEquip.length + appliedParts.length;

  // Build the query options with only the keys that are set — the hook's
  // option type is `exactOptionalPropertyTypes`-strict, so we omit rather than
  // pass `undefined`.
  const exercisesQ = useExercises({
    ...(q ? { search: q } : {}),
    ...(appliedParts.length ? { muscles: appliedParts } : {}),
    ...(appliedEquip.length ? { equipmentIds: appliedEquip } : {}),
  });
  const equipmentQ = useEquipment();
  const favsQ = useFavoriteExerciseIds();
  const toggleFav = useToggleFavorite();
  const archiveExercise = useArchiveExercise();

  const favs = favsQ.data ?? [];
  const equipment = equipmentQ.data ?? [];

  // Client-side rail narrowing on top of the server-filtered list.
  const exercises = (exercisesQ.data?.exercises ?? []).filter((ex) => {
    if (group === 'favorites') return favs.includes(ex.id);
    if (group === 'cardio') return ex.type === 'cardio_steady' || ex.type === 'cardio_intervals';
    if (group) return ex.primaryMuscle === group || ex.targetMuscles.includes(group);
    return true;
  });

  function openFilters(): void {
    setDraftEquip(appliedEquip);
    setDraftParts(appliedParts);
    setFiltersOpen(true);
  }

  function onToggleFav(ex: ExerciseDto): void {
    toggleFav.mutate({ id: ex.id, favored: favs.includes(ex.id) });
  }

  function onArchive(ex: ExerciseDto): void {
    archiveExercise.mutate(ex.id, {
      onSuccess: () => {
        setEditor(null);
        setDetail(null);
      },
    });
  }

  // The exercise editor takes over the page (mirrors the HealthWorkouts
  // EditorState overlay pattern) so the user gets a focused form.
  if (editor) {
    return (
      <div className={styles.exScreen}>
        {editor.kind === 'new' ? (
          <ExerciseEditor
            crumbLabel="Exercises"
            onCancel={() => setEditor(null)}
            onSaved={() => setEditor(null)}
          />
        ) : (
          <ExerciseEditor
            crumbLabel="Exercises"
            exercise={editor.exercise}
            onCancel={() => setEditor(null)}
            onSaved={() => setEditor(null)}
            onArchive={onArchive}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.exScreen}>
      {/* Header */}
      <div className={styles.exHeader}>
        <button
          type="button"
          className="kos-btn kos-btn-primary"
          onClick={() => setEditor({ kind: 'new' })}
        >
          <Icon name="plus" size={12} /> New exercise
        </button>
      </div>

      {/* Search */}
      <div className={styles.exSearch}>
        <Icon name="search" size={16} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search for exercises"
        />
      </div>

      {/* Muscle filter rail */}
      <div className={styles.exFilterRail}>
        <div className={styles.exFilterTrack}>
          {RAIL_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`${styles.exFilter} ${group === item.id ? styles.active : ''}`}
              onClick={() => setGroup(group === item.id ? null : item.id)}
            >
              <span className={styles.exFilterIcon}>
                <img src={item.icon} width={30} height={30} alt="" style={{ display: 'block' }} />
              </span>
              <span className={styles.exFilterLabel}>{item.label}</span>
            </button>
          ))}
          <button
            type="button"
            className={`${styles.exFilter} ${styles.exFilterSettings}`}
            onClick={openFilters}
          >
            <span className={styles.exFilterIcon} style={{ position: 'relative' }}>
              <img
                src="/health/muscles/filters.svg"
                width={30}
                height={30}
                alt=""
                style={{ display: 'block' }}
              />
              {activeFilterCount > 0 && (
                <span className={styles.exFilterBadge}>{activeFilterCount}</span>
              )}
            </span>
            <span className={styles.exFilterLabel}>Filters</span>
          </button>
        </div>
      </div>

      {/* Grid */}
      {exercises.length === 0 ? (
        <div className={baseStyles.empty} style={{ marginTop: 40 }}>
          {exercisesQ.isLoading ? 'Loading exercises…' : 'No exercises match.'}
        </div>
      ) : (
        <div className={styles.exGrid}>
          {exercises.map((ex) => (
            <ExerciseTile
              key={ex.id}
              ex={ex}
              fav={favs.includes(ex.id)}
              onOpen={setDetail}
              onToggleFav={onToggleFav}
            />
          ))}
        </div>
      )}

      {detail && (
        <ExerciseDetailModal
          ex={detail}
          equipment={equipment}
          fav={favs.includes(detail.id)}
          onToggleFav={onToggleFav}
          onEdit={(ex) => {
            setDetail(null);
            setEditor({ kind: 'edit', exercise: ex });
          }}
          onAddToWorkout={(ex) => setAddToWorkout(ex)}
          onClose={() => setDetail(null)}
        />
      )}

      {addToWorkout && (
        <AddToWorkoutModal
          ex={addToWorkout}
          onClose={() => setAddToWorkout(null)}
        />
      )}

      <FiltersModal
        open={filtersOpen}
        equipment={equipment}
        equip={draftEquip}
        parts={draftParts}
        onToggleEquip={(id) =>
          setDraftEquip((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]))
        }
        onTogglePart={(slug) =>
          setDraftParts((p) => (p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]))
        }
        onClear={() => {
          setDraftEquip([]);
          setDraftParts([]);
        }}
        onApply={() => {
          setAppliedEquip(draftEquip);
          setAppliedParts(draftParts);
          setFiltersOpen(false);
        }}
        onClose={() => setFiltersOpen(false)}
      />
    </div>
  );
}

/** Bookmark icon — outline, filled when favorited (the design's inline SVG). */
function BookmarkGlyph({ filled }: { filled: boolean }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill={filled ? 'var(--accent)' : 'none'}
      stroke="currentColor"
      strokeWidth="1.7"
    >
      <path d="M6 4h12v17l-6-4-6 4z" />
    </svg>
  );
}

function ExerciseTile({
  ex,
  fav,
  onOpen,
  onToggleFav,
}: {
  ex: ExerciseDto;
  fav: boolean;
  onOpen: (ex: ExerciseDto) => void;
  onToggleFav: (ex: ExerciseDto) => void;
}) {
  const subtitle =
    ex.targetMuscles.map(muscleLabel).join(', ') || muscleLabel(ex.primaryMuscle);
  return (
    <div className={styles.exTileWrap}>
      <button type="button" className={styles.exTile} onClick={() => onOpen(ex)}>
        <div className={styles.exTileTop}>
          <span className={styles.exTileHelp} aria-hidden="true">?</span>
        </div>
        <div className={styles.exTileFig}>
          <ExerciseFigure ex={ex} view="cover" size={150} />
        </div>
        <div className={styles.exTileName}>{ex.name}</div>
        <div className={styles.exTileMuscle}>{subtitle}</div>
      </button>
      <button
        type="button"
        className={`${styles.exTileBookmark} ${fav ? styles.on : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFav(ex);
        }}
        aria-label={fav ? 'Remove favorite' : 'Add favorite'}
      >
        <BookmarkGlyph filled={fav} />
      </button>
    </div>
  );
}

/** Extract a YouTube embed URL from any common YouTube link format. */
function youtubeEmbedUrl(url: string): string | null {
  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m?.[1]) return `https://www.youtube.com/embed/${m[1]}?autoplay=1`;
  }
  return null;
}

const FRONT_BODY = 'https://my.lyfta.app/images/muscleMap/front_grey_body.webp';
const BACK_BODY  = 'https://my.lyfta.app/images/muscleMap/back_grey_body.webp';

export function ExerciseDetailModal({
  ex,
  equipment,
  fav,
  onToggleFav,
  onEdit,
  onAddToWorkout,
  onClose,
}: {
  ex: ExerciseDto;
  equipment: EquipmentDto[];
  fav: boolean;
  onToggleFav: (ex: ExerciseDto) => void;
  onEdit?: (ex: ExerciseDto) => void;
  onAddToWorkout?: (ex: ExerciseDto) => void;
  onClose: () => void;
}) {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const embedUrl = ex.referenceUrl ? youtubeEmbedUrl(ex.referenceUrl) : null;
  const handlePlay = useCallback(() => setVideoPlaying(true), []);

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.exModal} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.exModalClose} onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
        <div className={styles.exModalGrid}>
          {/* Left: media */}
          <div className={styles.exModalMedia}>
            {/* Video / figure area */}
            {videoPlaying && embedUrl ? (
              <div className={styles.exModalVideoEmbed}>
                <iframe
                  src={embedUrl}
                  className={styles.exModalIframe}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`${ex.name} demo`}
                />
              </div>
            ) : (
              <div className={styles.exModalVideo}>
                <div className={styles.exModalFigbig}>
                  <ExerciseFigure ex={ex} view="cover" size={230} />
                </div>
                {ex.referenceUrl && (
                  <button
                    type="button"
                    className={styles.exModalPlay}
                    onClick={embedUrl ? handlePlay : () => window.open(ex.referenceUrl!, '_blank', 'noopener,noreferrer')}
                  >
                    <span className={styles.exModalPlayBtn}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                    <span>Watch demo</span>
                  </button>
                )}
              </div>
            )}
            {/* Front + Back thumbnails — unified block with grey body backgrounds */}
            <div className={styles.exModalThumbs}>
              <div className={styles.exModalThumb}>
                <div className={styles.exModalThumbBody}>
                  {ex.frontImageUrl && (
                    <img src={FRONT_BODY} className={styles.exModalThumbBg} alt="" aria-hidden="true" />
                  )}
                  <div className={styles.exModalThumbOverlay}>
                    <ExerciseFigure ex={ex} view="front" size={110} />
                  </div>
                </div>
                <span className={styles.exModalThumbLabel}>Front</span>
              </div>
              <div className={styles.exModalThumb}>
                <div className={styles.exModalThumbBody}>
                  {ex.backImageUrl && (
                    <img src={BACK_BODY} className={styles.exModalThumbBg} alt="" aria-hidden="true" />
                  )}
                  <div className={styles.exModalThumbOverlay}>
                    <ExerciseFigure ex={ex} view="back" size={110} />
                  </div>
                </div>
                <span className={styles.exModalThumbLabel}>Back</span>
              </div>
            </div>
          </div>

          {/* Right: details */}
          <div className={styles.exModalDetails}>
            <div className={styles.exModalHead}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className={styles.exModalTitle}>{ex.name}</h2>
                <div className={styles.exModalSub}>
                  {ex.targetMuscles.map(muscleLabel).join(' · ') || muscleLabel(ex.primaryMuscle)}
                </div>
              </div>
              {onEdit && (
                <button type="button" className="kos-btn" onClick={() => onEdit(ex)}>
                  Edit
                </button>
              )}
              <button
                type="button"
                className={`kos-btn ${fav ? 'kos-btn-primary' : ''}`}
                onClick={() => onToggleFav(ex)}
              >
                <BookmarkGlyph filled={fav} />
                {fav ? 'Saved' : 'Save'}
              </button>
            </div>

            {/* Meta — only Equipment / Type / Level exist on the DTO. */}
            <div className={styles.exModalMeta}>
              {(
                [
                  ['Equipment', equipmentName(ex.equipmentId, equipment)],
                  ['Type', typeLabel(ex.type)],
                  ['Level', levelLabel(ex.level)],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className={styles.exModalMetaItem}>
                  <span className={styles.exModalMetaK}>{k}</span>
                  <span className={styles.exModalMetaV}>{v}</span>
                </div>
              ))}
            </div>

            {ex.targetMuscles.length > 0 && (
              <div className={styles.exModalSection}>
                <div className={styles.exModalSectionTitle}>Target muscles</div>
                <div className={styles.exModalChips}>
                  {ex.targetMuscles.map((m) => (
                    <span key={m} className={`${styles.badge} ${styles.badgeSoft}`}>
                      {muscleLabel(m)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {ex.cues.length > 0 && (
              <div className={styles.exModalSection}>
                <div className={styles.exModalSectionTitle}>How to perform</div>
                <ol className={styles.exModalSteps}>
                  {ex.cues.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            <div className={styles.exModalActions}>
              {ex.referenceUrl && (
                <a
                  className={styles.refBtn}
                  href={ex.referenceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="sparkles" size={12} /> Watch video
                </a>
              )}
              {onAddToWorkout && (
                <button
                  type="button"
                  className="kos-btn kos-btn-primary"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => onAddToWorkout(ex)}
                >
                  <Icon name="plus" size={12} /> Add to workout
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Add-to-workout picker.
 *
 * Lists the user's workout templates; picking one appends THIS exercise to
 * that template's structure. Reuses the existing template-exercise mechanism
 * (`useReplaceTemplateExercises`) — we re-send the template's current
 * exercises with the new one appended at the end of its `defaultBlock`,
 * building the same payload shape WorkoutTemplateEditor uses. No new endpoint.
 */
function AddToWorkoutModal({
  ex,
  onClose,
}: {
  ex: ExerciseDto;
  onClose: () => void;
}) {
  const templatesQ = useWorkoutTemplates();
  const replaceExercises = useReplaceTemplateExercises();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const templates = templatesQ.data?.templates ?? [];

  function appendTo(tmpl: WorkoutTemplateDto): void {
    setError(null);
    // Guard against duplicates — if this exercise is already in the template,
    // do nothing (the row reflects the already-added state).
    if (tmpl.exercises.some((e) => e.exerciseId === ex.id)) return;
    // Re-send existing rows verbatim, then append this exercise to the end
    // of the block it defaults to — matching WorkoutTemplateEditor's payload.
    const existing = tmpl.exercises.map((e) => ({
      exerciseId: e.exerciseId,
      block: e.block,
      position: e.position,
      prescription: e.prescription ?? null,
      defaultSets: e.defaultSets,
      notes: e.notes ?? '',
    }));
    const newBlock = ex.defaultBlock;
    const exercises = [
      ...existing,
      {
        exerciseId: ex.id,
        block: newBlock,
        position: existing.filter((e) => e.block === newBlock).length + 1,
        prescription: ex.prescription ?? null,
        defaultSets: null,
        notes: '',
      },
    ];
    replaceExercises.mutate(
      { id: tmpl.id, exercises },
      {
        onSuccess: () => setDone(tmpl.name),
        onError: () => setError('Could not add to that workout. Please try again.'),
      },
    );
  }

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.filtersModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.filtersModalHead}>
          <h2>Add to workout</h2>
          <button
            type="button"
            className={styles.exModalClose}
            style={{ position: 'static' }}
            onClick={onClose}
          >
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className={styles.filtersModalBody}>
          <div className={styles.filtersSectionLabel}>{ex.name}</div>
          {done ? (
            <div className={baseStyles.empty} style={{ marginTop: 12 }}>
              Added to “{done}”.
            </div>
          ) : templatesQ.isLoading ? (
            <div className={baseStyles.empty} style={{ marginTop: 12 }}>
              Loading workouts…
            </div>
          ) : templates.length === 0 ? (
            <div className={baseStyles.empty} style={{ marginTop: 12 }}>
              No workout templates yet. Create one from the Workouts tab first.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {templates.map((t) => {
                const alreadyAdded = t.exercises.some((e) => e.exerciseId === ex.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={styles.filterCell}
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '10px 14px',
                      cursor: alreadyAdded ? 'default' : 'pointer',
                      opacity: alreadyAdded || replaceExercises.isPending ? 0.6 : 1,
                    }}
                    disabled={alreadyAdded || replaceExercises.isPending}
                    onClick={() => appendTo(t)}
                  >
                    <span className={styles.filterCellName} style={{ textAlign: 'left' }}>
                      {t.name}
                    </span>
                    {alreadyAdded ? (
                      <span
                        style={{
                          color: 'var(--accent)',
                          fontSize: 13,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Icon name="check" size={12} /> Already in this workout
                      </span>
                    ) : (
                      <span style={{ color: 'var(--ink-4)', fontSize: 13 }}>
                        {t.exercises.length} exercise{t.exercises.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {error && (
            <div style={{ color: 'var(--neg)', fontSize: 14, marginTop: 10 }}>
              {error}
            </div>
          )}
        </div>
        <div className={styles.filtersModalFoot}>
          <button type="button" className="kos-btn" onClick={onClose}>
            {done ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FiltersModal({
  open,
  equipment,
  equip,
  parts,
  onToggleEquip,
  onTogglePart,
  onClear,
  onApply,
  onClose,
}: {
  open: boolean;
  equipment: EquipmentDto[];
  equip: string[];
  parts: string[];
  onToggleEquip: (id: string) => void;
  onTogglePart: (slug: string) => void;
  onClear: () => void;
  onApply: () => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.filtersModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.filtersModalHead}>
          <h2>Filters</h2>
          <button
            type="button"
            className={styles.exModalClose}
            style={{ position: 'static' }}
            onClick={onClose}
          >
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className={styles.filtersModalBody}>
          <div className={styles.filtersSectionLabel}>Equipment</div>
          <div className={styles.filtersGrid}>
            {equipment.map((e) => {
              const sel = equip.includes(e.id);
              return (
                <button
                  key={e.id}
                  type="button"
                  className={`${styles.filterCell} ${sel ? styles.selected : ''}`}
                  onClick={() => onToggleEquip(e.id)}
                >
                  <span className={styles.filterCellCircle}>
                    <EquipGlyph iconKind={e.iconKind} selected={sel} />
                  </span>
                  <span className={styles.filterCellName}>{e.name}</span>
                </button>
              );
            })}
          </div>

          <div className={`${styles.filtersSectionLabel} ${styles.filtersSectionLabelSecond}`}>
            Body Part
          </div>
          <div className={styles.filtersGrid}>
            {MUSCLE_GROUPS.map((p) => {
              const sel = parts.includes(p.slug);
              return (
                <button
                  key={p.slug}
                  type="button"
                  className={`${styles.filterCell} ${sel ? styles.selected : ''}`}
                  onClick={() => onTogglePart(p.slug)}
                >
                  <span className={styles.filterCellCircle}>
                    <img
                      src={muscleIconSrc(p.slug) ?? ''}
                      width={30}
                      height={30}
                      alt=""
                      style={{ display: 'block' }}
                    />
                  </span>
                  <span className={styles.filterCellName}>{p.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.filtersModalFoot}>
          <button type="button" className="kos-btn" onClick={onClear}>
            Clear All
          </button>
          <button type="button" className="kos-btn kos-btn-primary" onClick={onApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
